import { Pub, Sub, State } from '../../../../shared/state/state.abstract.ts';
import { Observable, merge, switchMap, tap, BehaviorSubject, share, map } from 'rxjs';
import { GridSortModel } from '@mui/x-data-grid/models/gridSortModel';
import { ListBakery2bStatisticQueryRes, bakery2bService } from '../services/bakery2b.service.ts';
import { ListBakery2bStatisticQueryVariables } from '../../../../graphql/generatedModel.ts';
import { getFirstAndLastDateOfCurrentMonth } from '../../../../shared/helpers/utils/utils.helper.ts';
import { responseHandler } from '../../../../shared/responseHandler/responseHandler.ts';
import { dataHelper } from '../../../../shared/helpers/data/data.helper.ts';

export const initialBakery2bStatisticTabState: IBakery2bStatisticTabState = {
  action: undefined,
  search: '',
  statisticList: [],
  statisticListSortModel: [{ field: 'email', sort: 'asc' }],
  dateRange: getFirstAndLastDateOfCurrentMonth(),
  allStatisticListLength: 0,
};
class PubImpl extends Pub<IBakery2bStatisticTabState> {
  init(initState: Partial<IBakery2bStatisticTabState>): void {
    this.emit('init', { ...initState });
  }
  search(search: IBakery2bStatisticTabState['search']): void {
    this.emit('search', { search });
  }
  sort(sortModel: IBakery2bStatisticTabState['statisticListSortModel']): void {
    this.emit('sort', { statisticListSortModel: sortModel });
  }
  getStatisticList(dateRange: IBakery2bStatisticTabState['dateRange']): void {
    this.emit('getStatisticList', { dateRange });
  }
  refreshStatisticList(): void {
    this.emit('refreshStatisticList', {});
  }
}
class SubImpl extends Sub<IBakery2bStatisticTabState> {
  private gridLoading$ = new BehaviorSubject<boolean>(false);
  private shareGridLoading$: Observable<boolean> = this.gridLoading$.pipe(share());
  protected actionHandlers(): Observable<IBakery2bStatisticTabState> {
    return merge(this.updateState(), this.getStatisticList()).pipe(
      map(({ search, statisticList, statisticListSortModel, allStatisticListLength, ...rest }) => {
        let sortedStatisticList = structuredClone(statisticList);
        allStatisticListLength = statisticList.length;
        statisticList = dataHelper
          .data(statisticList as [])
          .sort({
            sortModel: statisticListSortModel,
            callback: (data) => {
              sortedStatisticList = data as [];
            },
          })
          .search({
            search,
            fields: ['email', 'name', 'newBakery2bOrders', 'orders', 'totalOrders'],
          })
          .result() as IBakery2bStatisticTabState['statisticList'];
        const updatedState = {
          ...rest,
          search,
          statisticList,
          statisticListSortModel,
          allStatisticListLength,
        };
        this.stream$.next({
          ...updatedState,
          statisticList: sortedStatisticList,
          action: 'internalUpdate',
        });
        return updatedState;
      }),
    );
  }
  gridLoading(): Observable<boolean> {
    return this.shareGridLoading$;
  }
  private getStatisticList(): Observable<IBakery2bStatisticTabState> {
    return this.actionListener(['getStatisticList', 'refreshStatisticList']).pipe(
      tap(({ dateRange }) => {
        this.gridLoading$.next(true);
        bakery2bService.pub.listBakery2bStatistic({ params: dateRange });
      }),
      switchMap(() => {
        return bakery2bService.sub.listBakery2bStatistic().pipe(
          responseHandler<ListBakery2bStatisticQueryRes>({
            errorReturnType: [],
          }),
        );
      }),
      map((statisticList) => {
        this.gridLoading$.next(false);
        const updatedState = structuredClone(this.stream$.getValue());
        updatedState.statisticList = statisticList;
        return updatedState;
      }),
    );
  }
  private updateState(): Observable<IBakery2bStatisticTabState> {
    return this.actionListener(['search', 'sort']);
  }
}

class Bakery2bStatisticState extends State<IBakery2bStatisticTabState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const bakery2bStatisticState = new Bakery2bStatisticState(
  structuredClone(initialBakery2bStatisticTabState),
);

export interface IBakery2bStatisticTabState {
  action:
    | 'init'
    | 'getStatisticList'
    | 'refreshStatisticList'
    | 'internalUpdate'
    | 'search'
    | 'sort'
    | undefined;
  search: string;
  statisticListSortModel: GridSortModel;
  statisticList: ListBakery2bStatisticQueryRes;
  allStatisticListLength: number;
  dateRange: ListBakery2bStatisticQueryVariables['params'];
}
